import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import CalendarColumnGrid from './CalendarColumnGrid';
import DatePickerModal from '../../../shared/DatePickerModal';
import { CALENDAR_VIEW_TYPES } from '../../../shared/appConstants';
import { getWeekDays, WEEKDAYS_FULL } from './calendarUtils';
import { getLocalDatePart, hasEffectiveDueTime } from '../../../shared/timeUtils';
import { fetchCalendarData, setCalendarViewType, setCalendarCurrentDate, setCalendarCustomDates } from '../../../store/calendarSlice';
import './CalendarView.scss';

const CalendarView = ({ onTaskClick, onCreateTask, selectedDate, projectId, filters = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { calendarData, loading, viewType, currentDate: storedDate, customDates } = useSelector(state => state.calendar);
  
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [colorCodeOption, setColorCodeOption] = React.useState(null);

  // Memoize currentDate to prevent unnecessary re-renders
  const currentDate = React.useMemo(() => new Date(storedDate), [storedDate]);
  
  // Ref to track last fetch parameters to prevent duplicate calls
  const lastFetchParamsRef = React.useRef(null);

  // Ref to track if custom dates were manually selected
  const manualCustomDatesRef = React.useRef(false);

  // Sync custom dates with current date when switching to custom view
  useEffect(() => {
    if (viewType === CALENDAR_VIEW_TYPES.CUSTOM) {
      // Only auto-generate dates if they weren't manually selected
      if (!manualCustomDatesRef.current && customDates.length === 0) {
        const dates = [];
        for (let i = 0; i < 4; i++) {
          const date = new Date(currentDate);
          date.setDate(currentDate.getDate() + i);
          dates.push(date.toISOString().split('T')[0]);
        }
        dispatch(setCalendarCustomDates(dates));
      }
    } else if (viewType !== CALENDAR_VIEW_TYPES.CUSTOM && customDates.length > 0) {
      // Reset manual flag when leaving custom view
      manualCustomDatesRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, dispatch]);

  // Update custom dates when currentDate changes in month/week view
  useEffect(() => {
    if (viewType !== CALENDAR_VIEW_TYPES.CUSTOM && !manualCustomDatesRef.current) {
      // Auto-update custom dates based on new currentDate
      const dates = [];
      for (let i = 0; i < 4; i++) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() + i);
        dates.push(date.toISOString().split('T')[0]);
      }
      dispatch(setCalendarCustomDates(dates));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedDate, viewType, dispatch]);

  // Fetch calendar data when date range or filters change
  useEffect(() => {
    if (!projectId) return;

    let fromDate, toDate;

    if (viewType === CALENDAR_VIEW_TYPES.MONTH) {
      const { getMonthDays } = require('./calendarUtils');
      const days = getMonthDays(currentDate);
      fromDate = days[0].dateString;
      toDate = days[days.length - 1].dateString;
    } else if (viewType === CALENDAR_VIEW_TYPES.WEEK) {
      const weekDays = getWeekDays(currentDate);
      fromDate = weekDays[0].dateString;
      toDate = weekDays[weekDays.length - 1].dateString;
    } else if (viewType === CALENDAR_VIEW_TYPES.CUSTOM) {
      if (customDates.length === 0) return;
      fromDate = customDates[0];
      toDate = customDates[customDates.length - 1];
    }

    if (fromDate && toDate) {
      // Create a unique key for this fetch to prevent duplicates
      const fetchKey = `${projectId}-${fromDate}-${toDate}-${viewType}-${JSON.stringify(filters)}`;
      
      // Only fetch if parameters have changed
      if (lastFetchParamsRef.current !== fetchKey) {
        lastFetchParamsRef.current = fetchKey;
        dispatch(fetchCalendarData({ projectId, fromDate, toDate, filters }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId, storedDate, viewType, customDates.length, JSON.stringify(filters)]);

  // Transform API data to match component format
  const tasks = useMemo(() => {
    return calendarData.flatMap(dayData =>
      dayData.tickets.map(ticket => {
        const ticketNumber = ticket.ticket_number ? String(ticket.ticket_number) : '';

        return {
          id: ticket.id,
          title: ticket.title,
          project_id: projectId,
          startDate: dayData.date,
          dueDate: getLocalDatePart(ticket.due_date),
          dueDateRaw: ticket.due_date,
          hasDueTime: hasEffectiveDueTime(ticket.has_due_time, ticket.due_date),
          priority: ticket.priority,
          status: ticket.status,
          taskType: ticket.task_type,
          assignee: ticket.assignee,
          assignees: ticket.assignees || [],
          ticketNumber,
          ticket_number: ticketNumber,
          code: ticketNumber,
          completed: ticket.marked_as_completed ?? ticket.completed ?? ticket.is_completed ?? false
        };
      })
    );
  }, [calendarData, projectId]);

  const notes = useMemo(() => {
    return calendarData.flatMap(dayData => 
      dayData.notes.map(note => ({
        id: note.id,
        date: dayData.date,
        text: note.text,
        assignee: note.assignee,
        linkedTicket: note.ticket,
        createdAt: note.created_at
      }))
    );
  }, [calendarData]);

  const handleDateChange = (newDate) => {
    dispatch(setCalendarCurrentDate(newDate.toISOString()));
    setDatePickerAnchorEl(null);
  };

  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);

    if (viewType === CALENDAR_VIEW_TYPES.MONTH) {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (viewType === CALENDAR_VIEW_TYPES.WEEK) {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewType === CALENDAR_VIEW_TYPES.CUSTOM) {
      const shiftDays = customDates.length || 1;
      newDate.setDate(newDate.getDate() + (direction * shiftDays));
      const dates = [];
      for (let i = 0; i < shiftDays; i++) {
        const date = new Date(newDate);
        date.setDate(newDate.getDate() + i);
        dates.push(date.toISOString().split('T')[0]);
      }
      manualCustomDatesRef.current = true;
      dispatch(setCalendarCustomDates(dates));
    }

    dispatch(setCalendarCurrentDate(newDate.toISOString()));
  };

  const handleCustomDateSelect = (dates) => {
    manualCustomDatesRef.current = true; // Mark as manually selected
    dispatch(setCalendarCustomDates(dates));
    if (dates.length > 0) {
      dispatch(setCalendarCurrentDate(new Date(dates[0]).toISOString()));
    }
    setDatePickerAnchorEl(null);
  };

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchorEl(event?.currentTarget || null);
  };

  const weekDates = useMemo(() => {
    if (viewType === CALENDAR_VIEW_TYPES.WEEK) {
      return getWeekDays(currentDate).map(day => day.dateString);
    }
    return [];
  }, [currentDate, viewType]);

  const customDateHeaders = useMemo(() => {
    if (viewType === CALENDAR_VIEW_TYPES.CUSTOM) {
      return customDates.map(dateStr => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'long' });
      });
    }
    return [];
  }, [customDates, viewType]);

  const monthYear = useMemo(() => {
    if (viewType === CALENDAR_VIEW_TYPES.CUSTOM && customDates.length > 0) {
      const firstDate = new Date(customDates[0]);
      const lastDate = new Date(customDates[customDates.length - 1]);
      const startDay = firstDate.getDate();
      const startMonth = firstDate.toLocaleDateString('en-US', { month: 'short' });
      const endDay = lastDate.getDate();
      const endMonth = lastDate.toLocaleDateString('en-US', { month: 'short' });
      const year = lastDate.getFullYear();
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} (${year})`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, [currentDate, viewType, customDates]);

  const weekRange = useMemo(() => {
    if (viewType === CALENDAR_VIEW_TYPES.WEEK) {
      const { getWeekRange } = require('./calendarUtils');
      return getWeekRange(currentDate);
    } else if (viewType === CALENDAR_VIEW_TYPES.CUSTOM && customDates.length > 0) {
      const firstDate = new Date(customDates[0]);
      const lastDate = new Date(customDates[customDates.length - 1]);
      const startDay = firstDate.getDate();
      const startMonth = firstDate.toLocaleDateString('en-US', { month: 'short' });
      const endDay = lastDate.getDate();
      const endMonth = lastDate.toLocaleDateString('en-US', { month: 'short' });
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
    return null;
  }, [currentDate, viewType, customDates]);

  // Memoize date range for AddNoteModal
  const dateRange = useMemo(() => {
    let fromDate, toDate;

    if (viewType === CALENDAR_VIEW_TYPES.MONTH) {
      const { getMonthDays } = require('./calendarUtils');
      const days = getMonthDays(currentDate);
      fromDate = days[0].dateString;
      toDate = days[days.length - 1].dateString;
    } else if (viewType === CALENDAR_VIEW_TYPES.WEEK) {
      const weekDays = getWeekDays(currentDate);
      fromDate = weekDays[0].dateString;
      toDate = weekDays[weekDays.length - 1].dateString;
    } else if (viewType === CALENDAR_VIEW_TYPES.CUSTOM) {
      fromDate = customDates[0];
      toDate = customDates[customDates.length - 1];
    }

    return { fromDate, toDate };
  }, [currentDate, viewType, customDates]);

  return (
    <Box className="calendar-view">
      <CalendarHeader
        monthYear={monthYear}
        weekRange={weekRange}
        viewType={viewType}
        onViewTypeChange={(type) => dispatch(setCalendarViewType(type))}
        onDatePickerOpen={handleDatePickerOpen}
        onWeekNavigate={handleNavigate}
        showWeekRange={viewType === CALENDAR_VIEW_TYPES.WEEK}
        colorCodeOption={colorCodeOption}
        onColorCodeChange={setColorCodeOption}
        tasks={tasks}
        dateRange={dateRange}
      />
      
      {loading ? (
        <Box className="calendar-loader">
          <CircularProgress />
          <Typography className="calendar-loader-text">{t('calendar.loadingCalendarData')}</Typography>
        </Box>
      ) : viewType === CALENDAR_VIEW_TYPES.MONTH ? (
        <CalendarGrid
          currentDate={currentDate}
          viewType={viewType}
          tasks={tasks}
          notes={notes}
          onTaskClick={onTaskClick}
          onCreateTask={onCreateTask}
          projectId={projectId}
          dateRange={dateRange}
          filters={filters}
          colorCodeOption={colorCodeOption}
        />
      ) : viewType === CALENDAR_VIEW_TYPES.WEEK ? (
        <CalendarColumnGrid
          dates={weekDates}
          headers={WEEKDAYS_FULL}
          tasks={tasks}
          notes={notes}
          onTaskClick={onTaskClick}
          onCreateTask={onCreateTask}
          projectId={projectId}
          dateRange={dateRange}
          filters={filters}
          colorCodeOption={colorCodeOption}
        />
      ) : (
        <CalendarColumnGrid
          dates={customDates}
          headers={customDateHeaders}
          tasks={tasks}
          notes={notes}
          onTaskClick={onTaskClick}
          onCreateTask={onCreateTask}
          projectId={projectId}
          dateRange={dateRange}
          filters={filters}
          colorCodeOption={colorCodeOption}
        />
      )}

      <DatePickerModal
        open={Boolean(datePickerAnchorEl)}
        anchorEl={datePickerAnchorEl}
        currentDate={currentDate}
        mode={viewType === CALENDAR_VIEW_TYPES.CUSTOM ? 'dateRange' : 'month'}
        title={
          viewType === CALENDAR_VIEW_TYPES.CUSTOM
            ? t('calendar.selectCustomRange')
            : t('gantt.selectMonth')
        }
        minDays={4}
        maxDays={10}
        helperText={viewType === CALENDAR_VIEW_TYPES.CUSTOM ? t('calendar.selectBetween4And10Days') : undefined}
        selectedDates={viewType === CALENDAR_VIEW_TYPES.CUSTOM ? customDates : undefined}
        onClose={() => setDatePickerAnchorEl(null)}
        onDateChange={viewType === CALENDAR_VIEW_TYPES.CUSTOM ? handleCustomDateSelect : handleDateChange}
      />
    </Box>
  );
};

export default CalendarView;
